"use client";

import React, { useState, useRef, useEffect } from "react";
import { Shield, KeyRound, LogOut, ChevronDown, Laptop, Globe } from "lucide-react";
import { useAuth } from "@/lib/auth/context";
import { useLanguage } from "@/lib/i18n/context";
import { ChangePasswordModal } from "../auth/ChangePasswordModal";
import { ActiveSessionsModal } from "../auth/ActiveSessionsModal";
import { UserProfileModal } from "../auth/UserProfileModal";

export function UserMenu() {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!user) {
    return null;
  }

  const initial = user.displayName ? user.displayName.charAt(0).toUpperCase() : user.username.charAt(0).toUpperCase();

  return (
    <>
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-xl bg-surface-muted hover:bg-surface-cardHover border border-themed-border hover:border-themed-borderAccent text-xs transition-colors focus:outline-none"
        >
          <div className="w-6 h-6 rounded-full bg-gradient-to-tr from-[var(--accent-primary)] to-[var(--accent-secondary)] text-[var(--accent-text)] font-bold flex items-center justify-center text-xs shadow-sm">
            {initial}
          </div>
          <div className="flex flex-col text-left">
            <span className="font-semibold text-themed-textPrimary text-xs leading-none">
              {user.displayName || user.username}
            </span>
            <span className="text-[10px] text-[var(--accent-primary)] font-mono leading-tight">
              {user.role}
            </span>
          </div>
          <ChevronDown className="w-3.5 h-3.5 text-themed-textMuted" />
        </button>

        {dropdownOpen && (
          <div className="absolute right-0 mt-2 w-60 bg-surface-elevated border border-themed-border rounded-2xl shadow-2xl py-2 z-50 animate-in fade-in-50 zoom-in-95 duration-100 text-xs backdrop-blur-xl">
            <div className="px-3.5 py-2 border-b border-themed-borderSubtle">
              <p className="text-[11px] text-themed-textMuted">{t("layout.userMenu.signedInAs")}</p>
              <p className="font-semibold text-themed-textPrimary truncate">{user.username}</p>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--accent-primary)]/10 border border-[var(--accent-primary)]/20 text-[var(--accent-primary)] text-[10px] font-semibold">
                  <Shield className="w-3 h-3" /> {user.role}
                </span>
                <span className="text-[10px] text-themed-textMuted font-mono">
                  {user.timezone || "UTC"}
                </span>
              </div>
            </div>

            <div className="py-1">
              <button
                onClick={() => {
                  setDropdownOpen(false);
                  setProfileOpen(true);
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-themed-textSecondary hover:text-themed-textPrimary hover:bg-surface-cardHover transition-colors text-left"
              >
                <Globe className="w-4 h-4 text-[var(--accent-primary)]" />
                <span>{t("layout.userMenu.profileAndPreferences")}</span>
              </button>

              <button
                onClick={() => {
                  setDropdownOpen(false);
                  setChangePasswordOpen(true);
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-themed-textSecondary hover:text-themed-textPrimary hover:bg-surface-cardHover transition-colors text-left"
              >
                <KeyRound className="w-4 h-4 text-[var(--accent-primary)]" />
                <span>{t("layout.userMenu.changePassword")}</span>
              </button>

              <button
                onClick={() => {
                  setDropdownOpen(false);
                  setSessionsOpen(true);
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-themed-textSecondary hover:text-themed-textPrimary hover:bg-surface-cardHover transition-colors text-left"
              >
                <Laptop className="w-4 h-4 text-[var(--accent-primary)]" />
                <span>{t("layout.userMenu.activeSessions")}</span>
              </button>
            </div>

            <div className="pt-1 border-t border-themed-borderSubtle">
              <button
                onClick={async () => {
                  setDropdownOpen(false);
                  await logout();
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-status-danger hover:bg-status-danger-bg transition-colors text-left font-medium"
              >
                <LogOut className="w-4 h-4" />
                <span>{t("layout.userMenu.signOut")}</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <UserProfileModal
        isOpen={profileOpen}
        onClose={() => setProfileOpen(false)}
      />

      <ChangePasswordModal
        isOpen={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />

      <ActiveSessionsModal
        isOpen={sessionsOpen}
        onClose={() => setSessionsOpen(false)}
      />
    </>
  );
}
